/**
 * Shared hook for managing bulk-download works with search/filter/date controls.
 *
 * Used by:
 *  - Non-premium upsell modal  (WorkCardsBulkDownloadUpsellModal/index.tsx)
 *  - Premium downloader modal   (PremiumWorkCardsBulkDownloadModal.tsx)
 *  - SUTD page                  (BulkDownloadPageWorkCards.tsx)
 *
 * All three share the same API endpoint (`works_api_v0_work_bulk_download_counts_path`)
 * and the same filter state management.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import $r from 'sitewide-js/core_pre_webpack/routes';

import { BundleWork, DateRangeFilter, SourceWork } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Shared debounce delay (ms) applied when the user types in the topic field. */
export const TOPIC_DEBOUNCE_MS = 500;

// ---------------------------------------------------------------------------
// Options & result types
// ---------------------------------------------------------------------------

/** Options accepted by the {@link useFilteredWorks} hook. */
export interface UseFilteredWorksOptions {
  /** The source work whose related papers we are fetching. */
  sourceWork: SourceWork;

  /** Debounce delay for topic query changes (default: {@link TOPIC_DEBOUNCE_MS}). */
  debounceMs?: number;

  /**
   * When `true`, skip all API calls and start with `loading = false`.
   * The consumer already has the data it needs (e.g., work IDs restored from
   * checkout sessionStorage).  Filters are still initialised so the UI can
   * display them, but no network requests are made.
   */
  hasPreloadedData?: boolean;

  /** Initial topic query (e.g., restored from checkout state). */
  initialTopicQuery?: string;
  /** Initial date range (e.g., restored from checkout state). */
  initialDateRange?: DateRangeFilter;
  /** Initial selected buckets (e.g., restored from checkout state). */
  initialSelectedBuckets?: string[];
}

/** Values returned by the {@link useFilteredWorks} hook. */
export interface UseFilteredWorksResult {
  // ── API data ──────────────────────────────────────────────────────────
  /** Raw bundle works array from the API (excludes the source work). */
  bundleWorks: BundleWork[];
  /** Full preview data for the source work, returned separately by the API. */
  sourceWorkPreview: BundleWork | undefined;
  /** Whether to show admin-only debug annotations (bucket, similarity). */
  showAnnotations: boolean;
  /** `true` while an API request is in flight. */
  loading: boolean;

  // ── Filter state ──────────────────────────────────────────────────────
  topicQuery: string;
  dateRange: DateRangeFilter;
  selectedBuckets: Set<string>;

  // ── Computed ───────────────────────────────────────────────────────────
  /** Related works only (source paper filtered out). */
  relatedWorks: BundleWork[];
  /** Sticky set of bucket names captured from the initial (unfiltered) load. */
  availableBuckets: Set<string>;

  // ── Actions ───────────────────────────────────────────────────────────
  setTopicQuery: (query: string) => void;
  setDateRange: (range: DateRangeFilter) => void;
  selectAllBuckets: () => void;
  toggleBucket: (bucket: string) => void;
  refetch: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

/**
 * Hook for managing bulk download works with filtering.
 *
 * @example
 * // Non-premium / SUTD — default behaviour (fetch on mount)
 * const { bundleWorks, loading, ... } = useFilteredWorks({ sourceWork });
 *
 * @example
 * // Premium modal — skip fetch when checkout IDs are available
 * const { bundleWorks, loading, ... } = useFilteredWorks({
 *   sourceWork,
 *   hasPreloadedData: downloadImmediately && !!checkoutWorkIds,
 *   initialTopicQuery: checkoutState?.topicQuery,
 * });
 */
export function useFilteredWorks({
  sourceWork,
  debounceMs = TOPIC_DEBOUNCE_MS,
  hasPreloadedData = false,
  initialTopicQuery,
  initialDateRange,
  initialSelectedBuckets,
}: UseFilteredWorksOptions): UseFilteredWorksResult {
  // ── State ───────────────────────────────────────────────────────────────
  const [bundleWorks, setBundleWorks] = useState<BundleWork[]>([]);
  const [sourceWorkPreview, setSourceWorkPreview] = useState<
    BundleWork | undefined
  >();
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [loading, setLoading] = useState(!hasPreloadedData);

  const [topicQuery, setTopicQuery] = useState(initialTopicQuery ?? '');
  const [dateRange, setDateRange] = useState<DateRangeFilter>(
    initialDateRange ?? 'all',
  );
  const [selectedBuckets, setSelectedBuckets] = useState<Set<string>>(
    () => new Set(initialSelectedBuckets ?? []),
  );

  // ── Derived ─────────────────────────────────────────────────────────────
  const bucketWhitelist = useMemo(() => {
    if (selectedBuckets.size === 0) return undefined;
    // Always include the source paper bucket so it stays visible.
    return Array.from(selectedBuckets).concat(['thePaperItself']);
  }, [selectedBuckets]);

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchWorks = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        setLoading(true);

        const url = $r.works_api_v0_work_bulk_download_counts_path({
          subdomain_param: 'api',
          work_id: sourceWork.id,
          ...(topicQuery ? { topic_query: topicQuery } : {}),
          ...(dateRange !== 'all' ? { date_range: dateRange } : {}),
          ...(bucketWhitelist ? { buckets: bucketWhitelist } : {}),
        });

        const response = await fetch(url, { signal });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setBundleWorks(data.bundleWorks || []);
        setSourceWorkPreview(data.sourceWorkPreview ?? undefined);
        setShowAnnotations(data.showAnnotations || false);
      } catch (error: unknown) {
        // Aborted requests are expected when filters change rapidly — ignore.
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        // eslint-disable-next-line no-console
        console.error('[useFilteredWorks] Failed to fetch works:', error);
        setBundleWorks([]);
        setSourceWorkPreview(undefined);
        setShowAnnotations(false);
      } finally {
        setLoading(false);
      }
    },
    [sourceWork.id, topicQuery, dateRange, bucketWhitelist],
  );

  // Debounced fetch effect — AbortController cancels stale in-flight requests.
  useEffect(() => {
    if (hasPreloadedData) return;

    const abortController = new AbortController();

    const timeout = window.setTimeout(
      async () => {
        await fetchWorks(abortController.signal);
      },
      topicQuery ? debounceMs : 0,
    );

    return () => {
      abortController.abort();
      window.clearTimeout(timeout);
    };
  }, [fetchWorks, topicQuery, debounceMs, hasPreloadedData]);

  // ── Computed ────────────────────────────────────────────────────────────

  // Sticky bucket set: captured from the first (unfiltered) API response so
  // that category chips remain visible even after the user selects a filter
  // and the backend returns only works in that bucket.
  const stickyBucketsRef = useRef<Set<string> | null>(null);

  // Reset when the source work changes so stale buckets from a previous
  // paper never leak through (defensive — the modal is typically remounted).
  useEffect(() => {
    stickyBucketsRef.current = null;
  }, [sourceWork.id]);

  const { relatedWorks, availableBuckets } = useMemo(() => {
    const works = bundleWorks ?? [];
    // bundleWorks should only contain related papers (the source paper comes
    // in sourceWorkPreview), but filter defensively.
    const related = works.filter((w) => w.bucket !== 'thePaperItself');
    const currentBuckets = new Set<string>();
    related.forEach((w) => {
      if (w.bucket) currentBuckets.add(w.bucket);
    });

    // Capture available buckets from the first non-empty response (the
    // initial "All" load).  Subsequent filtered responses won't shrink
    // the chip list.
    if (stickyBucketsRef.current === null && currentBuckets.size > 0) {
      stickyBucketsRef.current = currentBuckets;
    }

    return {
      relatedWorks: related,
      availableBuckets: stickyBucketsRef.current ?? currentBuckets,
    };
  }, [bundleWorks]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const selectAllBuckets = useCallback(() => {
    setSelectedBuckets(new Set());
  }, []);

  const toggleBucket = useCallback((bucket: string) => {
    setSelectedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucket)) {
        next.delete(bucket);
      } else {
        next.add(bucket);
      }
      return next;
    });
  }, []);

  // ── Return ──────────────────────────────────────────────────────────────
  return {
    bundleWorks,
    sourceWorkPreview,
    showAnnotations,
    loading,

    topicQuery,
    dateRange,
    selectedBuckets,

    relatedWorks,
    availableBuckets,

    setTopicQuery,
    setDateRange,
    selectAllBuckets,
    toggleBucket,
    refetch: fetchWorks,
  };
}

export default useFilteredWorks;
